import { useMemo } from "react";
import { useTranslation } from "src/i18n";
import type {
  AestheticsGateId,
  AestheticsReason,
  AestheticsReasonCode,
  GateResult,
} from "@CommonUtils/cdtStudio/domain/aestheticsScore";

/* Spelt out rather than built from a template so every reason and every check is guaranteed
   a translation in both languages. */
const REASON_KEY = {
  blockingPair: "planogramMetrics.aesthetics.reasons.blockingPair",
  blockingOne: "planogramMetrics.aesthetics.reasons.blockingOne",
  blockingPlain: "planogramMetrics.aesthetics.reasons.blockingPlain",
  ruleFence: "planogramMetrics.aesthetics.reasons.ruleFence",
  ruleMissing: "planogramMetrics.aesthetics.reasons.ruleMissing",
  rulePlain: "planogramMetrics.aesthetics.reasons.rulePlain",
  gap: "planogramMetrics.aesthetics.reasons.gap",
  fullnessPlain: "planogramMetrics.aesthetics.reasons.fullnessPlain",
  brandDropped: "planogramMetrics.aesthetics.reasons.brandDropped",
  unplaced: "planogramMetrics.aesthetics.reasons.unplaced",
  sizeOrder: "planogramMetrics.aesthetics.reasons.sizeOrder",
  orderPlain: "planogramMetrics.aesthetics.reasons.orderPlain",
  cdtPlain: "planogramMetrics.aesthetics.reasons.cdtPlain",
  heavyHigh: "planogramMetrics.aesthetics.reasons.heavyHigh",
  bigOverSmall: "planogramMetrics.aesthetics.reasons.bigOverSmall",
} as const satisfies Record<AestheticsReasonCode, string>;

const GATE_KEY = {
  overlap: "planogramMetrics.aesthetics.gates.overlap",
  "past-edge": "planogramMetrics.aesthetics.gates.past-edge",
  "empty-shelf": "planogramMetrics.aesthetics.gates.empty-shelf",
  unplaced: "planogramMetrics.aesthetics.gates.unplaced",
  "brand-eliminated": "planogramMetrics.aesthetics.gates.brand-eliminated",
  "block-in-pieces": "planogramMetrics.aesthetics.gates.block-in-pieces",
  "block-staircased": "planogramMetrics.aesthetics.gates.block-staircased",
  "block-out-of-shape": "planogramMetrics.aesthetics.gates.block-out-of-shape",
  "band-broken": "planogramMetrics.aesthetics.gates.band-broken",
  "shelves-not-full": "planogramMetrics.aesthetics.gates.shelves-not-full",
  "giant-gap": "planogramMetrics.aesthetics.gates.giant-gap",
} as const satisfies Record<AestheticsGateId, string>;

export const TOP_REASONS = 3;

/** A check that could not be evaluated is not a failure. */
export const failedGatesOf = (gates: readonly GateResult[]): readonly GateResult[] =>
  gates.filter((gate) => gate.evaluated && !gate.passed);

/** The Aesthetics score's reasons and checks in the merchant's own words, one wording for every surface. */
export const useAestheticsWording = () => {
  const { t } = useTranslation("common");
  return useMemo(
    () => ({
      reasonsTitle: t("planogramMetrics.aesthetics.reasonsTitle"),
      gatesTitle: t("planogramMetrics.aesthetics.gatesTitle"),
      reason: (reason: AestheticsReason) =>
        `${t("planogramMetrics.aesthetics.points", { points: reason.points })} ${t(
          REASON_KEY[reason.code],
          // A reason scored before lengths carried a unit says inches.
          { unit: "in", ...reason.values },
        )}`,
      /* A stored score can carry a check this build has no words for yet: it says the
         scorer's own label rather than crash the panel. */
      gate: (gate: Pick<GateResult, "id" | "label">) => {
        const known = Object.hasOwn(GATE_KEY, gate.id);
        return known ? t(GATE_KEY[gate.id]) : gate.label;
      },
    }),
    [t],
  );
};
